import { gsap } from "gsap";

/**
 * Hero Gallery Carousel
 * Fades between 4 hero images on mobile with green fade overlay
 */
class HeroGalleryCarousel {
  constructor(element) {
    this.element = element;
    this.images = Array.from(element.querySelectorAll('.hero-image-mobile'));
    this.currentIndex = 0;
    this.duration = 0.95; // Slide transition duration - fast and smooth
    this.overlayDuration = 1.1;
    this.interval = 3500; // Time between transitions - 3.5 seconds
    this.intervalId = null;
    this.hasStarted = false;

    if (this.images.length <= 1) return;

    this.init();
  }

  init() {
    // Add green overlay to each image and set initial states
    this.images.forEach((imageContainer, index) => {
      // Create green overlay element
      const overlay = document.createElement('div');
      overlay.className = 'carousel-green-overlay';
      overlay.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: #343B30;
        z-index: 3;
        pointer-events: none;
      `;
      imageContainer.appendChild(overlay);
      
      if (index === 0) {
        // First image: visible with green overlay ready
        gsap.set(imageContainer, { opacity: 1, xPercent: 0, zIndex: 2 });
        gsap.set(overlay, { opacity: 1 });
      } else {
        // Other images: staged off-canvas with green overlay ready
        gsap.set(imageContainer, { opacity: 0, xPercent: 100, zIndex: 1 });
        gsap.set(overlay, { opacity: 1 });
      }
    });

    this.startWhenTransitionClears();
  }

  startWhenTransitionClears() {
    const overlay = document.querySelector('.page-transition-overlay');

    if (!overlay || overlay.classList.contains('is-hidden')) {
      this.start();
      return;
    }

    const observer = new MutationObserver(() => {
      if (overlay.classList.contains('is-hidden')) {
        observer.disconnect();
        this.start();
      }
    });

    observer.observe(overlay, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  fadeOutGreen(overlay) {
    gsap.to(overlay, {
      opacity: 0,
      duration: this.overlayDuration,
      ease: 'power2.out'
    });
  }

  resetGreen(overlay) {
    gsap.set(overlay, { opacity: 1 });
  }

  start() {
    if (this.hasStarted) {
      return;
    }

    this.hasStarted = true;
    const firstOverlay = this.images[this.currentIndex].querySelector('.carousel-green-overlay');
    this.fadeOutGreen(firstOverlay);

    this.intervalId = setInterval(() => {
      this.fadeToNext();
    }, this.interval);
  }

  fadeToNext() {
    const currentImage = this.images[this.currentIndex];
    const currentOverlay = currentImage.querySelector('.carousel-green-overlay');
    
    this.currentIndex = (this.currentIndex + 1) % this.images.length;
    const nextImage = this.images[this.currentIndex];
    const nextOverlay = nextImage.querySelector('.carousel-green-overlay');

    // Slide the next green-covered image in, then fade the green away.
    gsap.set(nextImage, { opacity: 1, xPercent: 100, zIndex: 3 });
    this.resetGreen(nextOverlay);

    gsap.timeline({
      onComplete: () => {
        this.resetGreen(currentOverlay);
        gsap.set(currentImage, { opacity: 0, xPercent: 100, zIndex: 1 });
        gsap.set(nextImage, { opacity: 1, xPercent: 0, zIndex: 2 });
      }
    })
      .to(nextImage, {
        xPercent: 0,
        duration: this.duration,
        ease: 'power3.out'
      }, 0)
      .to(nextOverlay, {
        opacity: 0,
        duration: this.overlayDuration,
        ease: 'power2.out'
      }, this.duration);
  }

  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  destroy() {
    this.stop();
  }
}

// Auto-initialize on DOM ready
function initHeroGalleryCarousels() {
  const carousels = document.querySelectorAll('.hero-images-mobile');
  carousels.forEach(carousel => new HeroGalleryCarousel(carousel));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initHeroGalleryCarousels);
} else {
  initHeroGalleryCarousels();
}

export default HeroGalleryCarousel;
